import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldLabel } from "@/components/ui/field";
import { CallDirection, DatePreset, InsightsReportsFilters } from "@/types/insightsReports";
import { createDefaultInsightsFilters } from "@/types/insightsReports";
import type { InsightsFilterOptions } from "@/types/insightsApi";
import { addDays, addMonths, addWeeks, endOfDay, format, isEqual, subDays } from "date-fns";
import { CalendarIcon, X } from "lucide-react";

interface InsightsReportsFiltersProps {
  filters: InsightsReportsFilters;
  onFiltersChange: (filters: InsightsReportsFilters) => void;
  onRunReport: () => void;
  collapsed: boolean;
  filterOptions: InsightsFilterOptions | null;
  filterOptionsLoading: boolean;
}

function applyPreset(from: Date, preset: DatePreset): Date {
  switch (preset) {
    case "day":
      return addDays(from, 1);
    case "week":
      return addWeeks(from, 1);
    case "month":
      return addMonths(from, 1);
  }
}

function DateTimeInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder: string;
}) {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between gap-2">
            <CalendarIcon className="text-muted-foreground size-3.5 shrink-0" />
            <span className="truncate">{value ? format(value, "dd MMM yyyy, HH:mm") : placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} defaultMonth={value} captionLayout="dropdown" />
          {value && (
            <div className="border-t p-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-muted-foreground text-xs">Time</label>
                <Input
                  type="time"
                  value={value ? format(value, "HH:mm") : ""}
                  onChange={(e) => {
                    if (!value || !e.target.value) return;
                    const [hours, minutes] = e.target.value.split(":").map(Number);
                    if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
                    const updated = new Date(value);
                    updated.setHours(hours, minutes, 0, 0);
                    onChange(updated);
                  }}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </Field>
  );
}

export function InsightsReportsFiltersBar({
  filters,
  onFiltersChange,
  onRunReport,
  collapsed,
  filterOptions,
  filterOptionsLoading,
}: InsightsReportsFiltersProps) {
  const defaultEndDate = endOfDay(subDays(new Date(), 2));
  const set = <K extends keyof InsightsReportsFilters>(key: K, value: InsightsReportsFilters[K]) =>
    onFiltersChange({ ...filters, [key]: value });

  // Convert string[] from API to { label, value }[] for MultiSelect
  const toOptions = (items: string[]) => items.map((s) => ({ label: s, value: s }));
  const queueOptions = toOptions(filterOptions?.queues ?? []);
  const agentOptions = toOptions(filterOptions?.agents ?? []);
  const departmentOptions = toOptions(filterOptions?.departments ?? []);

  const isModified =
    filters.callDirection !== "both" ||
    filters.customerPhone !== "" ||
    filters.taskQueues.length > 0 ||
    filters.departments.length > 0 ||
    filters.agents.length > 0 ||
    filters.startDate !== undefined ||
    filters.endDate === undefined ||
    !isEqual(filters.endDate, defaultEndDate) ||
    filters.datePreset !== undefined;

  const handleStartDateChange = (d: Date | undefined) => {
    const endDate = d && filters.datePreset ? applyPreset(d, filters.datePreset) : filters.endDate;
    onFiltersChange({ ...filters, startDate: d, endDate });
  };

  const handlePresetChange = (preset: DatePreset | undefined) => {
    const endDate = filters.startDate && preset ? applyPreset(filters.startDate, preset) : filters.endDate;
    onFiltersChange({ ...filters, datePreset: preset, endDate });
  };

  return (
    <div className="border-b px-4 py-3">
      {!collapsed && (
        <>
          <div className="flex h-7 items-center justify-between">
            <span className="text-muted-foreground text-sm font-medium">Filters</span>
            <div className="flex items-center gap-2">
              {isModified && !collapsed && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onFiltersChange(createDefaultInsightsFilters())}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <X className="size-3" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-4">
            {/* Row 1: dropdowns + text input */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Customer Phone</label>
                <Input
                  placeholder="Phone number..."
                  value={filters.customerPhone}
                  onChange={(e) => set("customerPhone", e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Task Queue</label>
                <MultiSelect
                  options={queueOptions}
                  onValueChange={(v) => set("taskQueues", v)}
                  defaultValue={filters.taskQueues}
                  placeholder={filterOptionsLoading ? "Loading…" : "All queues"}
                  disabled={filterOptionsLoading}
                  maxCount={1}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Department</label>
                <MultiSelect
                  options={departmentOptions}
                  onValueChange={(v) => set("departments", v)}
                  defaultValue={filters.departments}
                  placeholder={filterOptionsLoading ? "Loading…" : "All departments"}
                  disabled={filterOptionsLoading}
                  maxCount={1}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Agent</label>
                <MultiSelect
                  options={agentOptions}
                  onValueChange={(v) => set("agents", v)}
                  defaultValue={filters.agents}
                  placeholder={filterOptionsLoading ? "Loading…" : "All agents"}
                  disabled={filterOptionsLoading}
                  maxCount={1}
                />
              </div>
            </div>

            {/* Row 2: call direction + preset + date range */}
            <div className="flex flex-wrap items-end gap-3">
              {/* Call Direction */}
              <div className="flex shrink-0 flex-col gap-1.5">
                <label className="text-sm font-medium">Call Direction</label>
                <Tabs value={filters.callDirection} onValueChange={(v) => set("callDirection", v as CallDirection)}>
                  <TabsList>
                    <TabsTrigger value="both">Both</TabsTrigger>
                    <TabsTrigger value="inbound">Inbound</TabsTrigger>
                    <TabsTrigger value="outbound">Outbound</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Date Preset */}
              <div className="flex shrink-0 flex-col gap-1.5">
                <label className="text-sm font-medium">Period</label>
                <Tabs
                  value={filters.datePreset ?? "none"}
                  onValueChange={(v) => handlePresetChange(v === "none" ? undefined : (v as DatePreset))}
                >
                  <TabsList>
                    <TabsTrigger value="none">—</TabsTrigger>
                    <TabsTrigger value="day">Day</TabsTrigger>
                    <TabsTrigger value="week">Week</TabsTrigger>
                    <TabsTrigger value="month">Month</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Date range */}
              <div className="flex flex-1 items-end gap-3">
                <div className="min-w-0 flex-1">
                  <DateTimeInput
                    label="From"
                    value={filters.startDate}
                    onChange={handleStartDateChange}
                    placeholder="Start date & time"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <DateTimeInput
                    label="To"
                    value={filters.endDate}
                    onChange={(d) => set("endDate", d)}
                    placeholder="End date & time"
                  />
                </div>
                <Button onClick={onRunReport} className="shrink-0 self-end">
                  Run Report
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
